import useAppDispatch from "../../hooks/use-app-dispatch";
import useAppSelector from "../../hooks/use-app-selector";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { clearAnswer } from "../../app/slices/question-slice";
import { useIsAskAdmin } from "../../hooks/use-is-ask-admin";
import { useIsSuperAdmin } from "../../hooks/use-is-super-admin";
import {
    ChatBubbleLeftRightIcon,
    ClockIcon,
    Cog6ToothIcon,
    WrenchScrewdriverIcon,
    DocumentIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";

export default () => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isAdmin } = useIsAskAdmin();
    const { isSuperAdmin } = useIsSuperAdmin();
    const { questionAsking } = useAppSelector((store) => store.question);

    const ask = pathname === "/ask";
    const isAsking = Boolean(questionAsking);
    const documentLibrary = pathname.startsWith("/document-library");
    const files = pathname === "/files";
    const settings = pathname === "/settings" || pathname.startsWith("/settings/");
    const admin = pathname === "/admin";
    const superAdmin = pathname === "/super-admin";

    const handleAskClick = () => {
        if (pathname === "/ask") {
            if (isAsking) return;
            dispatch(clearAnswer());
        } else {
            if (!isAsking) dispatch(clearAnswer());
            navigate("/ask");
        }
    };

    const iconClass = (active: boolean) =>
        cn("w-5 h-5", {
            "text-lite-blue": active,
            "text-lite-grey": !active,
        });

    return (
        <div className="hidden lg:flex flex-row items-center px-6 gap-8">
            <div
                className={cn(
                    "flex flex-row items-center gap-2 py-3 border-b-[3px] cursor-pointer",
                    { "border-lite-blue": ask },
                    { "border-transparent": !ask },
                )}
                onClick={handleAskClick}
            >
                <ChatBubbleLeftRightIcon className={iconClass(ask)} />

                <p
                    className={cn("font-bold", {
                        "text-lite-blue": ask,
                        "text-lite-grey": !ask,
                    })}
                >
                    ASK
                </p>
            </div>

            <Link to="/document-library">
                <div
                    className={cn(
                        "flex flex-row items-center gap-2 py-3 border-b-[3px]",
                        { "border-lite-blue": documentLibrary },
                        { "border-transparent": !documentLibrary },
                    )}
                >
                    <ClockIcon className={iconClass(documentLibrary)} />

                    <p
                        className={cn("font-bold", {
                            "text-lite-blue": documentLibrary,
                            "text-lite-grey": !documentLibrary,
                        })}
                    >
                        History
                    </p>
                </div>
            </Link>

            <Link to="/files">
                <div
                    className={cn(
                        "flex flex-row items-center gap-2 py-3 border-b-[3px]",
                        { "border-lite-blue": files },
                        { "border-transparent": !files },
                    )}
                >
                    <DocumentIcon className={iconClass(files)} />
                    <p
                        className={cn("font-bold", {
                            "text-lite-blue": files,
                            "text-lite-grey": !files,
                        })}
                    >
                        Files
                    </p>
                </div>
            </Link>

            <Link to="/settings">
                <div
                    className={cn(
                        "flex flex-row items-center gap-2 py-3 border-b-[3px]",
                        { "border-lite-blue": settings },
                        { "border-transparent": !settings },
                    )}
                >
                    <Cog6ToothIcon className={iconClass(settings)} />

                    <p
                        className={cn("font-bold", {
                            "text-lite-blue": settings,
                            "text-lite-grey": !settings,
                        })}
                    >
                        Settings
                    </p>
                </div>
            </Link>

            {isAdmin && (
                <Link to="/admin">
                    <div
                        className={cn(
                            "flex flex-row items-center gap-2 py-3 border-b-[3px]",
                            { "border-lite-blue": admin },
                            { "border-transparent": !admin },
                        )}
                    >
                        <WrenchScrewdriverIcon className={iconClass(admin)} />

                        <p
                            className={cn("font-bold", {
                                "text-lite-blue": admin,
                                "text-lite-grey": !admin,
                            })}
                        >
                            Admin
                        </p>
                    </div>
                </Link>
            )}

            {isSuperAdmin && (
                <Link to="/super-admin">
                    <div
                        className={cn(
                            "flex flex-row items-center gap-2 py-3 border-b-[3px]",
                            { "border-lite-blue": superAdmin },
                            { "border-transparent": !superAdmin },
                        )}
                    >
                        <ShieldCheckIcon className={iconClass(superAdmin)} />

                        <p
                            className={cn("font-bold", {
                                "text-lite-blue": superAdmin,
                                "text-lite-grey": !superAdmin,
                            })}
                        >
                            Super Admin
                        </p>
                    </div>
                </Link>
            )}
        </div>
    );
};
